/**
 * @file upp-konva-host-draw.ts
 * Host <-> draw integration for the Konva stage.
 *
 * Encapsulates canvas callbacks and keeps internal stage bridges and per-shape
 * observers (**`onSelected`**, **`onUpdated`**) aligned with the current model
 * state.
 */

import type {
  UppFloorPlanElementClickPayload,
  UppFloorPlanElementGeometryPayload,
  UppFloorPlanRoomBackgroundPointerPayload,
  UppFloorPlanVertexDoubleClickPayload,
  UppFloorPlanStageComponent,
} from '../canvas/upp-floor-plan-stage.component';
import {
  floorPlanPresentationGeometryPartition,
  type FloorPlanGeometrySnapshot,
} from '../presentation/floor-plan-presentation-geometry';
import type { UppKonvaFloorPlanModel } from '../internal/upp-konva-floor-plan-model';
import { konvaShapeByUuidFromModel } from '../internal/upp-konva-model-resolve-shape';
import {
  stageInvokeActivate,
  stageInvokeGeometry,
  stageInvokeSelection,
} from '../internal/upp-konva-stage-host-bridge';
import { uppKonvaSceneModelHost } from '../internal/upp-konva-scene-model-host';
import { roundFloorPlanGeometryCm, roundNormalizedPlanRotationDeg } from '../geometry/floor-plan-konva-sync';
import { emitUppKonvaGroupedFromStage } from './upp-konva-host-group';

/**
 * Draw event adapter between stage, model, and Konva shapes.
 */
export class UppKonvaHostDrawIntegration {
  private readonly _geometrySnapshotById = new Map<string, FloorPlanGeometrySnapshot>();

  /**
   * Initializes the geometry snapshot cache from the model.
   *
   * @param model Current model or **`undefined`**.
   */
  seedGeometryCacheFromModel(model: UppKonvaFloorPlanModel | undefined): void {
    this._geometrySnapshotById.clear();
    this.syncGeometrySnapshotsFromElements(model);
  }

  /**
   * @brief Updates UUID snapshots from model scene elements.
   *
   * @param model Current model or **`undefined`** (no-op).
   */
  syncGeometrySnapshotsFromElements(model: UppKonvaFloorPlanModel | undefined): void {
    if (!model) {
      return;
    }
    for (const el of uppKonvaSceneModelHost(model).sceneModel.elements) {
      this._geometrySnapshotById.set(el.uuid, {
        x: el.xCm,
        y: el.yCm,
        w: el.widthCm,
        h: el.heightCm,
        r: el.rotationDeg,
      });
    }
  }

  /**
   * Propagates selection changes to the internal stage and grouping logic.
   *
   * @param model Current model.
   * @param floor Internal stage.
   * @param elementUuids Selected UUIDs.
   */
  onSelectionChange(
    model: UppKonvaFloorPlanModel | undefined,
    floor: UppFloorPlanStageComponent | undefined,
    elementUuids: readonly string[],
  ): void {
    if (model) {
      stageInvokeSelection(model, elementUuids);
    }
    if (model && floor) {
      emitUppKonvaGroupedFromStage(model, floor);
    }
  }

  /**
   * Propagates activation/click for a stage element.
   *
   * @param model Current model.
   * @param payload Stage payload.
   */
  onActivate(model: UppKonvaFloorPlanModel | undefined, payload: UppFloorPlanElementClickPayload): void {
    if (model) {
      stageInvokeActivate(model, payload);
    }
    if (model?.mode === 'VIEW') {
      const sh = konvaShapeByUuidFromModel(model, payload.element.uuid);
      if (payload.element.kind === 'bar') {
        sh?.onSelected?.({ custom: sh.custom, stool: payload.stool ?? null });
      } else {
        sh?.onSelected?.({ custom: sh.custom });
      }
    }
  }

  /**
   * Handles room-background double click and syncs follow-up selection.
   *
   * @param model Current model.
   * @param floor Stage for **`selectOnly`** in a microtask.
   * @param payload Coordinates in cm.
   */
  onRoomBackgroundDoubleClick(
    model: UppKonvaFloorPlanModel | undefined,
    floor: UppFloorPlanStageComponent | undefined,
    payload: UppFloorPlanRoomBackgroundPointerPayload,
  ): void {
    if (!model) {
      return;
    }
    const id = uppKonvaSceneModelHost(model).onRoomBackgroundDoubleClickCm(payload.xCm, payload.yCm);
    if (id) {
      queueMicrotask(() => floor?.selectOnly([id]));
    }
  }

  /**
   * @brief Single click on room background (close chain / discard draft).
   *
   * @param model Current model.
   * @param _payload Unused payload (signature compatibility).
   */
  onRoomBackgroundSingleClick(
    model: UppKonvaFloorPlanModel | undefined,
    _payload: UppFloorPlanRoomBackgroundPointerPayload,
  ): void {
    void _payload;
    if (model) {
      uppKonvaSceneModelHost(model).onRoomBackgroundSingleClick();
    }
  }

  /**
   * Handles vertex double click and forces element reselection.
   *
   * @param model Current model.
   * @param floor Stage for **`selectOnly`**.
   * @param payload Payload for the clicked vertex.
   */
  onVertexDoubleClick(
    model: UppKonvaFloorPlanModel | undefined,
    floor: UppFloorPlanStageComponent | undefined,
    payload: UppFloorPlanVertexDoubleClickPayload,
  ): void {
    if (!model) {
      return;
    }
    uppKonvaSceneModelHost(model).onVertexDoubleClick(payload.element.uuid);
    queueMicrotask(() => floor?.selectOnly([payload.element.uuid]));
  }

  /**
   * Applies a geometry commit and returns whether a relevant presentation delta
   * occurred. If there are no changes, returns `false` (e.g. Konva echo without
   * delta). The **`upp-konva-floor-plan`** host should emit **`(onChange)`**
   * only when this method returns `true`, to avoid duplicate sync cycles.
   */
  onGeometryCommit(model: UppKonvaFloorPlanModel | undefined, payload: UppFloorPlanElementGeometryPayload): boolean {
    const id = payload.element.uuid;
    const prev = this._geometrySnapshotById.get(id);
    const { emitTranslate, emitRotate, next } = floorPlanPresentationGeometryPartition(prev, payload);
    this._geometrySnapshotById.set(id, next);
    if (!emitTranslate && !emitRotate) {
      return false;
    }
    if (model) {
      stageInvokeGeometry(model, payload);
    }
    if (model?.mode === 'EDIT') {
      const sh = konvaShapeByUuidFromModel(model, id);
      if (sh) {
        const nextRotation = roundNormalizedPlanRotationDeg(Number(payload.rotationDeg));
        sh.rotation = nextRotation;
        /**
         * `onUpdated` consumes the current `shape` state. If emitted with stale
         * coordinates, integrators that serialize `shape -> DrawItem` (e.g. floor
         * tab) persist obsolete values.
         */
        if (emitTranslate) {
          /**
           * Defensive rounding avoids floating-point drift in Konva
           */
          sh.x = roundFloorPlanGeometryCm(Number(payload.xCm));
          sh.y = roundFloorPlanGeometryCm(Number(payload.yCm));
          sh.width = roundFloorPlanGeometryCm(Number(payload.widthCm));
          sh.height = roundFloorPlanGeometryCm(Number(payload.heightCm));
        }
        sh.onUpdated?.({ custom: sh.custom });
      }
    }
    return true;
  }
}
